import Button from '@mui/material/Button';
import { InputLabel, MenuItem, Select } from '@mui/material';
import moment from 'moment';
import React, {useState} from 'react';
import FHGTextField from '../../components/TextField';
import ModalDialog from '../../fhg/components/dialog/ModalDialog';
import Grid from '../../fhg/components/Grid';
import FHGTypography from '../../fhg/components/Typography';
import {BUG_REPORT_STATUS_COLORS, BUG_REPORT_STATUS_LABELS, BUG_REPORT_STATUSES} from './BugReports';

/**
 * Detail dialog for a single bug report - screenshots, a status selector (triggers bugReport_UpdateStatus
 * immediately on change, no separate save step), and the clarification comment thread with a reply box
 * (bugReportComment_Create, always posted as an admin reply).
 *
 * @param bugReport The bug report to display (already includes attachments/comments).
 * @param isSaving Indicates a status change or reply is in flight, to disable the status Select/reply button.
 * @param onChangeStatus Callback(status) when the admin picks a new status.
 * @param onPostComment Callback(message) when the admin submits a reply.
 * @param onClose Close the dialog.
 */
export default function BugReportDetail({bugReport, isSaving, onChangeStatus, onPostComment, onClose}) {
   const [replyText, setReplyText] = useState('');

   const screenshots = bugReport.attachments?.filter(
      attachment => !attachment.attachmentType || attachment.attachmentType === 'screenshot') || [];
   const logFile = bugReport.attachments?.find(attachment => attachment.attachmentType === 'log');

   const metadataLines = [
      bugReport.appVersion && `App Version: ${bugReport.appVersion}`,
      bugReport.platform && `Platform: ${bugReport.platform}`,
      bugReport.deviceModel && `Device: ${bugReport.deviceModel}`,
      bugReport.testUUID && `Test UUID: ${bugReport.testUUID}`,
   ].filter(Boolean);

   const handleChangeStatus = (event) => {
      onChangeStatus(event.target.value);
   };

   const handleChangeReplyText = (event) => {
      setReplyText(event.target.value);
   };

   const handleSendReply = () => {
      if (replyText.trim()) {
         onPostComment(replyText.trim());
         setReplyText('');
      }
   };

   return (
      <ModalDialog open titleKey={'bugReportDetail.title'} onClose={onClose} cancelKey={'close.button'}
         maxWidth={'sm'} fullWidth>
         <Grid container direction={'column'} wrap={'nowrap'} style={{gap: 16}}>
            <FHGTypography variant={'body1'}>{bugReport.description}</FHGTypography>

            {metadataLines.length > 0 && (
               <Grid container direction={'column'} wrap={'nowrap'} style={{gap: 2}}>
                  {metadataLines.map(line => (
                     <FHGTypography key={line} variant={'caption'} color={'textSecondary'}>{line}</FHGTypography>
                  ))}
               </Grid>
            )}

            {screenshots.length > 0 && (
               <Grid container direction={'row'} wrap={'wrap'} style={{gap: 8}}>
                  {screenshots.map(attachment => (
                     <a key={attachment.id} href={attachment.url} target={'_blank'} rel={'noreferrer'}>
                        <img src={attachment.url} alt={attachment.originalFileName || 'screenshot'}
                           style={{width: 100, height: 100, objectFit: 'cover', borderRadius: 4}}/>
                     </a>
                  ))}
               </Grid>
            )}

            {logFile && (
               <Button component={'a'} href={logFile.url} target={'_blank'} rel={'noreferrer'}
                  color={'primary'} variant={'outlined'} size={'small'} style={{alignSelf: 'flex-start'}}>
                  Download log file{logFile.originalFileName ? ` (${logFile.originalFileName})` : ''}
               </Button>
            )}

            <Grid item>
               <InputLabel id={'bugReportStatusLabel'} style={{color: 'black', fontWeight: 500, fontSize: 14}}>
                  <FHGTypography id={'bugReportDetail.status.label'}/>
               </InputLabel>
               <Select labelId={'bugReportStatusLabel'} value={bugReport.status} onChange={handleChangeStatus}
                  disabled={isSaving} fullWidth>
                  {BUG_REPORT_STATUSES.map(status => (
                     <MenuItem key={status} value={status}>
                        <span style={{
                           display: 'inline-block', width: 10, height: 10, borderRadius: '50%', marginRight: 8,
                           backgroundColor: BUG_REPORT_STATUS_COLORS[status],
                        }}/>
                        {BUG_REPORT_STATUS_LABELS[status]}
                     </MenuItem>
                  ))}
               </Select>
            </Grid>

            <Grid item container direction={'column'} wrap={'nowrap'} style={{gap: 8}}>
               <FHGTypography variant={'subtitle2'} id={'bugReportDetail.comments.label'}/>
               {(!bugReport.comments || bugReport.comments.length === 0) && (
                  <FHGTypography variant={'body2'} color={'textSecondary'} id={'bugReportDetail.noComments'}/>
               )}
               {bugReport.comments?.map(comment => (
                  <Grid key={comment.id} item style={{
                     alignSelf: comment.isFromAdmin ? 'flex-end' : 'flex-start',
                     backgroundColor: comment.isFromAdmin ? '#DCEEFF' : '#EDEDED',
                     borderRadius: 8, padding: '8px 12px', maxWidth: '80%',
                  }}>
                     <FHGTypography variant={'caption'} style={{fontWeight: 'bold', display: 'block'}}>
                        {comment.isFromAdmin ? 'Support' : (comment.createdByUser?.name || comment.createdByUser?.email || 'Reporter')}
                     </FHGTypography>
                     <FHGTypography variant={'body2'}>{comment.message}</FHGTypography>
                     <FHGTypography variant={'caption'} color={'textSecondary'}>
                        {moment(comment.createdDateTime, 'x').format('M/D/YYYY h:mm A')}
                     </FHGTypography>
                  </Grid>
               ))}
            </Grid>

            <Grid item container direction={'row'} wrap={'nowrap'} alignItems={'center'} style={{gap: 8}}>
               <Grid item resizable style={{flex: '1 1 auto'}}>
                  <FHGTextField withLabel={false} fullWidth placeholder={'Type a reply...'} value={replyText}
                     onChange={handleChangeReplyText} disabled={isSaving}/>
               </Grid>
               <Button color={'primary'} variant={'contained'} onClick={handleSendReply}
                  disabled={isSaving || !replyText.trim()}>
                  <FHGTypography noWrap id={'bugReportDetail.reply.button'}/>
               </Button>
            </Grid>
         </Grid>
      </ModalDialog>
   );
}
